'use client'

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { login } from '@/lib/utilsServer';
import { Loader2 } from 'lucide-react';
import DemoCredentials from '@/components/DemoCredentials';
import { createClient } from '@/lib/supabaseClient';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const router = useRouter();
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    // Check for error in URL hash (from OAuth errors)
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const errorDescription = hashParams.get('error_description');
    if (errorDescription) {
      setError(decodeURIComponent(errorDescription.replace(/\+/g, ' ')));
    }

    // Check for message in URL query params (from invite flow)
    const searchParams = new URLSearchParams(window.location.search);
    const message = searchParams.get('message');
    if (message === 'link_expired') {
      setInfo('Your invitation link has expired. Please log in to access your account, or request a new invitation link.');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const result = await login({ email, password });

    if (result.error) {
      setError(result.error);
      setIsLoading(false);
    } else if (result.success) {
      // Keep loading state active until redirect completes
      router.push('/');
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError(null);
    const supabase = createClient();
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (oauthError) {
      setError(oauthError.message);
      setIsGoogleLoading(false);
    }
  };

  const handleDemoFill = (demoEmail: string, demoPassword: string) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
  };

  const handleDemoSubmit = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <div className="flex flex-col justify-center items-center">
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 w-full px-8">
        <Input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          required
        />
        <Input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          required
        />
        {info && <p className="text-primary mt-4 p-3 bg-primary/10 rounded-md border border-primary/30">{info}</p>}
        {error && <p className="text-destructive mt-4">{error}</p>}
        <Button className='w-full' type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing In...
            </>
          ) : (
            "Sign In"
          )}
        </Button>
        <div className="flex items-center gap-2 my-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">OR</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
        >
          {isGoogleLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.997 10.997 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.36-2.1V7.07H2.18A10.997 10.997 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
          )}
          Continue with Google
        </Button>
      </form>
      <div className="mt-4 flex flex-col items-center gap-2">
        <Link href="/auth/login-email" className="text-muted-foreground hover:text-foreground hover:underline">
          Login with email only
        </Link>
        <Link href="/auth/forgot-password" className="text-muted-foreground hover:text-foreground hover:underline">
          Forgot password?
        </Link>
      </div>
      <DemoCredentials onFill={handleDemoFill} onSubmit={handleDemoSubmit} />
      <p className="mt-16 text-muted-foreground">
        Don't have an account?{' '}
        <Link href="/auth/signup" className="text-muted-foreground hover:text-foreground hover:underline">
          Sign up
        </Link>
      </p>
    </div>
  );
}
